/**
 * Artifacts side-panel — lifts a long, structured assistant reply (a multi-step
 * reissue procedure, a long fare-rule extract, a block of GDS command lines) out
 * of the scrolling transcript into a dedicated, independently-scrollable pane,
 * so the advisor can keep chatting while the procedure stays put. Claude
 * Artifacts / ChatGPT Canvas, in the Nomad design system.
 *
 * LAYOUT (the load-bearing decision, see ChatPage.tsx):
 *   - Wide (>= lg, 1024px): a fixed-width right column whose width is store-
 *     driven and user-resizable via the left-edge drag handle. The transcript
 *     simply narrows; nothing remounts, so toggling/resizing never reflows the
 *     thread's scroll position mid-stream.
 *   - Narrow (< lg): an OVERLAY (absolute, full-width, with a scrim) instead of
 *     squeezing the transcript to nothing. (The dock threshold is lg, not md,
 *     because the fixed w-72 sidebar already eats 288px — see useMediaQuery.ts.)
 *
 * The panel reads which artifact to show from the session store (open +
 * messageIndex), re-derives it from the live draft message via the same pure
 * detector the thread uses, and renders a document as <Markdown> or code as a
 * bordered <pre>. Header carries Copy + Download + Close, all with aria-labels.
 *
 * Esc-to-dismiss is handled centrally in useKeyboardShortcuts (so it composes
 * correctly with Esc-stops-streaming), NOT with a competing listener here.
 */
import {
  CheckIcon,
  CopyIcon,
  DownloadSimpleIcon,
  XIcon,
} from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSessionStore } from '@/features/chat/store/sessionStore';
import {
  ARTIFACT_MAX_WIDTH,
  ARTIFACT_MIN_WIDTH,
} from '@/features/chat/store/sessionStore';
import { Button, ScrollArea } from '@andavo/web-ui';
import { type Artifact, detectArtifact } from '../artifact/detect';
import { downloadArtifact } from '../artifact/export';
import { ARTIFACT_WIDE_QUERY, useMediaQuery } from '../hooks/useMediaQuery';
import { Markdown } from './Markdown';

/** Keyboard step (px) when nudging the resize handle with arrow keys. */
const RESIZE_STEP = 24;

/** Renders the artifact body — markdown for a document, a bordered pre for code. */
function ArtifactBody({ artifact }: { artifact: Artifact }) {
  if (artifact.kind === 'code') {
    return (
      <pre
        className="overflow-x-auto rounded-md border border-border-subtle bg-surface-background p-3 font-mono text-content-primary text-xs"
        data-testid="artifact-code"
      >
        <code>{artifact.content}</code>
      </pre>
    );
  }
  return (
    <div
      className="font-inter text-content-primary text-sm"
      data-testid="artifact-document"
    >
      <Markdown>{artifact.content}</Markdown>
    </div>
  );
}

export function ArtifactPanel({
  suppressOverlay = false,
}: {
  /**
   * Below lg, render nothing even if the store says open: the conversations
   * sheet owns the surface (see narrowSurface.ts). Derived by the page in the
   * same render as the sheet's own visibility, so the two cannot overlap.
   * Ignored at >= lg, where the panel is a docked column.
   */
  suppressOverlay?: boolean;
} = {}) {
  const { open, width, messageIndex } = useSessionStore((s) => s.artifactPanel);
  const draftMessages = useSessionStore((s) => s.draftMessages);
  const closeArtifact = useSessionStore((s) => s.closeArtifact);
  const setArtifactWidth = useSessionStore((s) => s.setArtifactWidth);
  const isWide = useMediaQuery(ARTIFACT_WIDE_QUERY);

  const [copied, setCopied] = useState(false);
  const draggingRef = useRef(false);
  const rafRef = useRef<number | null>(null);

  // Re-derive the artifact from the live message so the panel reflects edits /
  // regenerations of the same turn. Null if the message is gone or no longer
  // qualifies (e.g. it was regenerated into a short answer) — then we close.
  const message =
    messageIndex != null ? draftMessages[messageIndex] : undefined;
  const artifact =
    message && message.role === 'assistant'
      ? detectArtifact(message.content)
      : null;

  // If the panel is open but its artifact vanished, close it (avoids an empty
  // pane lingering after the underlying message changed/disappeared).
  useEffect(() => {
    if (open && !artifact) closeArtifact();
  }, [open, artifact, closeArtifact]);

  // ── Pointer drag resize (wide layout only) ──
  const onPointerMove = useCallback(
    (e: PointerEvent) => {
      if (!draggingRef.current) return;
      // The handle sits on the panel's LEFT edge: dragging left grows the panel.
      const next = window.innerWidth - e.clientX;
      if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => setArtifactWidth(next));
    },
    [setArtifactWidth],
  );

  const endDrag = useCallback(() => {
    draggingRef.current = false;
    if (rafRef.current != null) cancelAnimationFrame(rafRef.current);
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', endDrag);
    document.body.style.userSelect = '';
  }, [onPointerMove]);

  const startDrag = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      draggingRef.current = true;
      document.body.style.userSelect = 'none';
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', endDrag);
    },
    [onPointerMove, endDrag],
  );

  // Cleanup any in-flight drag listeners on unmount.
  useEffect(() => endDrag, [endDrag]);

  const onResizeKey = useCallback(
    (e: React.KeyboardEvent) => {
      // The handle grows the panel to the LEFT, so ArrowLeft = wider.
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setArtifactWidth(width + RESIZE_STEP);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setArtifactWidth(width - RESIZE_STEP);
      } else if (e.key === 'Home') {
        e.preventDefault();
        setArtifactWidth(ARTIFACT_MAX_WIDTH);
      } else if (e.key === 'End') {
        e.preventDefault();
        setArtifactWidth(ARTIFACT_MIN_WIDTH);
      }
    },
    [width, setArtifactWidth],
  );

  const copy = useCallback(async () => {
    if (!artifact) return;
    try {
      await navigator.clipboard.writeText(artifact.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable (insecure context) — silent, matches MessageActions */
    }
  }, [artifact]);

  if (!open || !artifact) return null;
  if (!isWide && suppressOverlay) return null;

  const header = (
    <div className="flex items-center justify-between gap-2 border-border-subtle border-b px-4 py-2">
      <span
        className="truncate font-inter font-medium text-content-primary text-sm"
        title={artifact.title}
      >
        {artifact.title}
      </span>
      <div className="flex shrink-0 items-center gap-1">
        <Button
          aria-label="Copy artifact"
          className="h-7 gap-1 px-2 text-content-secondary"
          data-testid="artifact-panel-copy"
          onClick={() => {
            void copy();
          }}
          size="sm"
          variant="ghost"
        >
          {copied ? (
            <CheckIcon className="size-icon-sm" />
          ) : (
            <CopyIcon className="size-icon-sm" />
          )}
          <span className="text-xs">{copied ? 'Copied' : 'Copy'}</span>
        </Button>
        <Button
          aria-label="Download artifact"
          className="h-7 gap-1 px-2 text-content-secondary"
          data-testid="artifact-panel-download"
          onClick={() => downloadArtifact(artifact)}
          size="sm"
          variant="ghost"
        >
          <DownloadSimpleIcon className="size-icon-sm" />
          <span className="text-xs">Download</span>
        </Button>
        <Button
          aria-label="Close artifact panel"
          className="h-7 px-2 text-content-secondary"
          data-testid="artifact-panel-close"
          onClick={closeArtifact}
          size="sm"
          variant="ghost"
        >
          <XIcon className="size-icon-sm" />
        </Button>
      </div>
    </div>
  );

  const body = (
    <ScrollArea className="flex-1">
      <div className="p-4">
        <ArtifactBody artifact={artifact} />
      </div>
    </ScrollArea>
  );

  // ── Narrow: overlay + scrim ──
  if (!isWide) {
    return (
      <div className="absolute inset-0 z-30 flex" data-testid="artifact-panel">
        {/* Scrim — tapping it dismisses the panel. */}
        <button
          aria-label="Close artifact panel"
          className="flex-1 bg-black/30"
          onClick={closeArtifact}
          tabIndex={-1}
          type="button"
        />
        <aside
          aria-label="Artifact"
          className="flex w-[88%] max-w-[560px] flex-col border-border-subtle border-l bg-surface-foreground shadow-lg"
        >
          {header}
          {body}
        </aside>
      </div>
    );
  }

  // ── Wide: resizable side column ──
  return (
    <aside
      aria-label="Artifact"
      className="relative flex shrink-0 flex-col border-border-subtle border-l bg-surface-foreground"
      data-testid="artifact-panel"
      style={{ width }}
    >
      {/* Left-edge resize handle. */}
      <div
        aria-label="Resize artifact panel"
        aria-orientation="vertical"
        aria-valuemax={ARTIFACT_MAX_WIDTH}
        aria-valuemin={ARTIFACT_MIN_WIDTH}
        aria-valuenow={width}
        className="-left-1 absolute top-0 bottom-0 z-10 w-2 cursor-col-resize hover:bg-border-subtle"
        data-testid="artifact-panel-resize"
        onKeyDown={onResizeKey}
        onPointerDown={startDrag}
        role="separator"
        tabIndex={0}
      />
      {header}
      {body}
    </aside>
  );
}
